"use strict";
import { useCallback, useMemo } from "react";
import { Currency } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { FiatCurrency } from "features/fiatCurrency/constants";
import { getFiatCurrencyCode, useAppFiatCurrency } from "features/fiatCurrency/hooks";
const _mapServerCurrencyToFiatCurrency = {
  [Currency.Aud]: FiatCurrency.AustralianDollar,
  [Currency.Brl]: FiatCurrency.BrazilianReal,
  [Currency.Cad]: FiatCurrency.CanadianDollar,
  [Currency.Cny]: FiatCurrency.ChineseYuan,
  [Currency.Eur]: FiatCurrency.Euro,
  [Currency.Gbp]: FiatCurrency.BritishPound,
  [Currency.Hkd]: FiatCurrency.HongKongDollar,
  [Currency.Idr]: FiatCurrency.IndonesianRupiah,
  [Currency.Inr]: FiatCurrency.IndianRupee,
  [Currency.Jpy]: FiatCurrency.JapaneseYen,
  [Currency.Krw]: FiatCurrency.SouthKoreanWon,
  [Currency.Ngn]: FiatCurrency.NigerianNaira,
  [Currency.Pkr]: FiatCurrency.PakistaniRupee,
  [Currency.Rub]: FiatCurrency.RussianRuble,
  [Currency.Sgd]: FiatCurrency.SingaporeDollar,
  [Currency.Thb]: FiatCurrency.ThaiBaht,
  [Currency.Try]: FiatCurrency.TurkishLira,
  [Currency.Uah]: FiatCurrency.UkrainianHryvnia,
  [Currency.Usd]: FiatCurrency.UnitedStatesDollar,
  [Currency.Vnd]: FiatCurrency.VietnameseDong,
  [Currency.Eth]: void 0,
  [Currency.Matic]: void 0
};
export const mapFiatCurrencyToServerCurrency = {
  [FiatCurrency.AustralianDollar]: Currency.Aud,
  [FiatCurrency.BrazilianReal]: Currency.Brl,
  [FiatCurrency.CanadianDollar]: Currency.Cad,
  [FiatCurrency.ChineseYuan]: Currency.Cny,
  [FiatCurrency.Euro]: Currency.Eur,
  [FiatCurrency.BritishPound]: Currency.Gbp,
  [FiatCurrency.HongKongDollar]: Currency.Hkd,
  [FiatCurrency.IndonesianRupiah]: Currency.Idr,
  [FiatCurrency.IndianRupee]: Currency.Inr,
  [FiatCurrency.JapaneseYen]: Currency.Jpy,
  [FiatCurrency.SouthKoreanWon]: Currency.Krw,
  [FiatCurrency.NigerianNaira]: Currency.Ngn,
  [FiatCurrency.PakistaniRupee]: Currency.Pkr,
  [FiatCurrency.RussianRuble]: Currency.Rub,
  [FiatCurrency.SingaporeDollar]: Currency.Sgd,
  [FiatCurrency.ThaiBaht]: Currency.Thb,
  [FiatCurrency.TurkishLira]: Currency.Try,
  [FiatCurrency.UkrainianHryvnia]: Currency.Uah,
  [FiatCurrency.UnitedStatesDollar]: Currency.Usd,
  [FiatCurrency.VietnameseDong]: Currency.Vnd
};
const SOURCE_CURRENCY = Currency.Usd;
export function useFiatConverter({
  formatNumberOrString
}) {
  const appCurrency = useAppFiatCurrency();
  const toCurrency = mapFiatCurrencyToServerCurrency[appCurrency];
  const conversionRate = 1;
  const outputCurrency = appCurrency;
  const convertFiatAmountInner = useCallback(
    (amount) => {
      const defaultResult = { amount, currency: FiatCurrency.UnitedStatesDollar };
      if (SOURCE_CURRENCY === toCurrency || !conversionRate || !outputCurrency) {
        return defaultResult;
      }
      return {
        amount: amount * conversionRate,
        currency: outputCurrency
      };
    },
    [conversionRate, outputCurrency, toCurrency]
  );
  const convertFiatAmountFormattedInner = useCallback(
    (fromAmount, numberType, placeholder = "-") => {
      if (fromAmount === void 0 || fromAmount === null) {
        return placeholder;
      }
      const amountNumber = typeof fromAmount === "string" ? parseFloat(fromAmount) : fromAmount;
      const converted = convertFiatAmountInner(amountNumber);
      const currencyCode = getFiatCurrencyCode(converted.currency);
      return formatNumberOrString({
        value: converted.amount,
        type: numberType,
        currencyCode,
        placeholder
      });
    },
    [convertFiatAmountInner, formatNumberOrString]
  );
  return useMemo(
    () => ({
      conversionRate,
      convertFiatAmount: convertFiatAmountInner,
      convertFiatAmountFormatted: convertFiatAmountFormattedInner
    }),
    [conversionRate, convertFiatAmountFormattedInner, convertFiatAmountInner]
  );
}
